/* eslint-disable */
import {Pressable, ScrollView, StyleSheet, Text, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Toast from 'react-native-toast-message';
import PrimaryButton from '../../components/PrimaryButton';
import {card, colors, radii, spacing, typography} from '../../config/theme';
import {FILL_UPS_TEXT, PLAN} from '../../config/onboardingData';
import {answerQuestions} from '../../slice/answerSlice';
import {saveOnboardingAnswers, saveOnboardingLocation} from '../../services/onboarding-service';
import AppIcon from '../../components/onboarding/AppIcon';
import {useOnboarding} from './OnboardingContext';

const SavingsSummary = ({navigation}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const {
    answers,
    currency: CURRENCY,
    selectedFrequency: option,
    selectedFuel,
  } = useOnboarding();

  // Yearly savings come from the API (the selected frequency's forecast).
  // Monthly, net profit and ROI are calculated here.
  // PLAN (app price, trial days) is NOT in the API yet, see onboardingData.js.
  const yearly = option.forecast.max; // e.g. 340
  const monthly = option.monthlySavings ?? Math.round((yearly / 12) * 10) / 10;
  const net = monthly - PLAN.price; // e.g. 25.31
  const roi = Math.round(monthly / PLAN.price); // e.g. 9

  const handleStartTrial = async () => {
    if (saving) return; // ignore double taps
    setSaving(true);
    try {
      // TODO: start the free trial (app-store / play billing) BEFORE saving.

      // 1) Save the answers. This is what makes the backend mark the user as done.
      //    We send the options actually shown as selected (the API's defaults
      //    count too, even if the user never tapped them).
      await saveOnboardingAnswers({
        frequency: option,
        fuelType: selectedFuel,
        multipleVehicles: answers.multipleVehicles,
        location: answers.location,
      });
      await saveOnboardingLocation(answers.location);
      // 2) Ask the backend again ("user/get-profile" -> has_answered).
      //    When it returns true, Navigation.js switches to the main app.
      const result = await dispatch(answerQuestions());
      console.log('[onboarding] has_answered after saving:', result?.payload);

      if (answerQuestions.rejected.match(result)) {
        // the profile check itself failed (network / token)
        Toast.show({
          type: 'error',
          text1: result.payload || 'Could not check your account. Please try again.',
        });
      } else if (!result.payload) {
        // the save worked, but the backend still says "not answered"
        Toast.show({
          type: 'error',
          text1: 'Answers sent, but the server still shows setup as not complete.',
        });
      }
    } catch (e) {
      Toast.show({
        type: 'error',
        text1: e?.message || 'Could not save your answers. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    {
      key: 'today',
      icon: 'check',
      done: true,
      title: t('onboarding.summary.today', 'TODAY'),
      badge: t('onboarding.summary.free', 'Free Access'),
      body: t(
        'onboarding.summary.todayBody',
        '{{currency}}0 charged today. Unlock all premium stations and tanker delivery.',
        {currency: CURRENCY},
      ),
    },
    {
      key: 'reminder',
      icon: 'bell',
      title: `${t('onboarding.summary.day', 'DAY')} ${PLAN.reminderDay}`,
      body: t(
        'onboarding.summary.reminderBody',
        'Friendly reminder notification sent. Zero surprise renewals.',
      ),
    },
    {
      key: 'plan',
      icon: 'bolt',
      title: `${t('onboarding.summary.day', 'DAY')} ${PLAN.trialDays}`,
      body: t(
        'onboarding.summary.planBody',
        '{{price}}/mo standard plan starts. Cancel anytime in 1 tap from settings.',
        {price: `${CURRENCY}${PLAN.price.toFixed(2)}`},
      ),
    },
  ];

  return (
    <SafeAreaView style={styles.safe}>
      {/* Fixed header: back arrow on the left, pill in the middle */}
      <View style={styles.header}>
        <Pressable
          onPress={() => !saving && navigation.goBack()}
          hitSlop={12}
          style={styles.back}>
          <Text style={styles.backIcon}>←</Text>
        </Pressable>
        <View style={styles.readyPill}>
          <AppIcon name="sparkles" size={14} />
          <Text style={styles.readyText}>
            {t('onboarding.summary.ready', 'YOUR PERSONAL SAVINGS READY')}
          </Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}>
        {/* Hero */}
        <View style={styles.hero}>
          <View style={styles.verified}>
            <AppIcon name="leaf" size={13} color={colors.onPrimaryMuted} />
            <Text style={styles.verifiedText}>
              {t('onboarding.summary.verified', 'VERIFIED CALCULATION')}
            </Text>
          </View>

          <View style={styles.amountRow}>
            <Text style={styles.currency}>{CURRENCY}</Text>
            <Text style={styles.amount}>{yearly}</Text>
          </View>
          <Text style={styles.savedEvery}>
            {t('onboarding.summary.savedEvery', 'SAVED EVERY YEAR')}
          </Text>
          <Text style={styles.basedOn}>
            {t('onboarding.summary.basedOn', 'Based on your')} {FILL_UPS_TEXT[option.id] || option.label}{' '}
            {t('onboarding.summary.withPompi', 'with Pompi live station routing')}
          </Text>

          <View style={styles.dispatch}>
            <AppIcon name="benzine" size={15} color={colors.onPrimary} />
            <Text style={styles.dispatchText}>
              {t('onboarding.summary.dispatch', 'Smart Pump Dispatch Active')}
            </Text>
            <View style={styles.liveDot} />
          </View>
        </View>

        {/* Profit breakdown */}
        <View style={styles.breakdown}>
          <View style={styles.line}>
            <View style={styles.lineLeft}>
              <View style={styles.lineIcon}>
                <AppIcon name="trendUp" size={18} />
              </View>
              <Text style={styles.lineLabel}>
                {t('onboarding.summary.monthlySavings', 'Monthly Fuel Savings')}
              </Text>
            </View>
            <Text style={styles.plus}>
              +{CURRENCY}
              {monthly.toFixed(2)} / mo
            </Text>
          </View>

          <View style={styles.line}>
            <View style={styles.lineLeft}>
              <View style={styles.lineIcon}>
                <AppIcon name="card" size={18} />
              </View>
              <Text style={styles.lineLabelMuted}>
                {t('onboarding.summary.proApp', 'Pompi Pro App')}
              </Text>
            </View>
            <Text style={styles.minus}>
              -{CURRENCY}
              {PLAN.price.toFixed(2)} / mo
            </Text>
          </View>

          <View style={styles.divider} />

          <View style={styles.netRow}>
            <Text style={styles.netLabel}>
              ★ {t('onboarding.summary.net', 'YOUR NET PROFIT')}
            </Text>
            <Text style={styles.netValue}>
              +{CURRENCY}
              {net.toFixed(2)} / mo
            </Text>
          </View>

          <View style={styles.roiPill}>
            <AppIcon name="flame" size={15} color={colors.onPrimary} />
            <Text style={styles.roiText}>
              {roi}X {t('onboarding.summary.roi', 'RETURN ON INVESTMENT')}
            </Text>
          </View>
        </View>

        {/* Free trial timeline */}
        <View style={styles.timeline}>
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            return (
              <View key={step.key} style={styles.step}>
                <View style={styles.stepLeft}>
                  <View style={[styles.stepIcon, step.done && styles.stepIconOn]}>
                    <AppIcon
                      name={step.icon}
                      size={16}
                      color={step.done ? colors.onPrimary : colors.primary}
                    />
                  </View>
                  {!isLast && <View style={styles.stepLine} />}
                </View>
                <View style={[styles.stepText, !isLast && styles.stepTextGap]}>
                  <View style={styles.stepTitleRow}>
                    <Text style={styles.stepTitle}>{step.title}</Text>
                    {!!step.badge && (
                      <View style={styles.freeBadge}>
                        <Text style={styles.freeBadgeText}>{step.badge}</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.stepBody}>{step.body}</Text>
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <PrimaryButton
          title={
            saving
              ? t('onboarding.summary.saving', 'SAVING…')
              : t('onboarding.summary.cta', 'START 30-DAY FREE TRIAL →')
          }
          onPress={handleStartTrial}
        />
        <Text style={styles.footnote}>
          {CURRENCY}0 {t('onboarding.summary.today', 'today').toLowerCase()} •{' '}
          {t('onboarding.summary.cancel', 'Cancel anytime')} •{' '}
          {t('onboarding.summary.risk', '100% Risk Free')}
        </Text>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safe: {flex: 1, backgroundColor: colors.background},
  scroll: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
    gap: spacing.md,
  },
  header: {
    height: 48,
    marginTop: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  back: {
    position: 'absolute',
    left: spacing.margin,
    height: 48,
    width: 40,
    justifyContent: 'center',
  },
  backIcon: {...typography.headlineLg, color: colors.primary},
  readyPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'center',
    backgroundColor: colors.mintSoft,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md + 2,
    paddingVertical: spacing.xs + 3,
  },
  readyText: {...typography.labelSm, color: colors.primary},

  // Hero
  hero: {
    backgroundColor: colors.primary,
    borderRadius: radii.sheet,
    padding: spacing.lg,
    alignItems: 'center',
    gap: spacing.sm,
  },
  verified: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 1,
  },
  verifiedText: {...typography.labelSm, color: colors.onPrimaryMuted},
  // lineHeight must be ~1.3x the font size for this font, or the top of the
  // digits gets clipped on Android.
  amountRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginTop: spacing.sm,
  },
  amount: {
    ...typography.displayLg,
    fontSize: 64,
    lineHeight: 84,
    letterSpacing: -1,
    color: colors.onPrimary,
  },
  currency: {
    ...typography.headlineLg,
    color: colors.onPrimaryMuted,
    marginTop: 16,
    marginRight: 4,
  },
  savedEvery: {...typography.headlineSm, color: colors.tertiary},
  basedOn: {
    ...typography.bodySm,
    color: colors.onPrimaryMuted,
    textAlign: 'center',
  },
  dispatch: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primaryDeep,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
  },
  dispatchText: {...typography.labelMd, color: colors.onPrimary},
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: colors.tertiary,
  },

  // Breakdown card
  breakdown: {...card, padding: spacing.md + 4, gap: spacing.md},
  line: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lineLeft: {flexDirection: 'row', alignItems: 'center', gap: spacing.md},
  lineIcon: {
    width: 36,
    height: 36,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lineEmoji: {fontSize: 16},
  lineLabel: {...typography.labelLg, color: colors.text},
  lineLabelMuted: {...typography.bodyMd, color: colors.textMuted},
  plus: {...typography.labelLg, color: colors.secondary},
  minus: {...typography.labelLg, color: colors.textMuted},
  divider: {height: 1, backgroundColor: colors.border},
  netRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.mintSoft,
    borderRadius: radii.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  netLabel: {...typography.labelMd, color: colors.primary},
  netValue: {...typography.headlineSm, color: colors.secondary},
  roiPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'center',
    backgroundColor: colors.primary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
  },
  roiText: {...typography.labelMd, color: colors.onPrimary},

  // Timeline
  timeline: {
    backgroundColor: colors.surfaceMuted,
    borderRadius: radii.card,
    padding: spacing.md + 4,
  },
  step: {flexDirection: 'row', gap: spacing.md},
  stepLeft: {alignItems: 'center'},
  stepLine: {
    flex: 1,
    width: 2,
    marginVertical: 4,
    borderRadius: 1,
    backgroundColor: colors.border,
  },
  stepTextGap: {paddingBottom: spacing.md + 4},
  stepIcon: {
    width: 32,
    height: 32,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepIconOn: {backgroundColor: colors.secondary, borderColor: colors.secondary},
  stepCheck: {fontSize: 14, color: colors.onPrimary},
  stepEmoji: {fontSize: 14},
  stepText: {flex: 1, gap: 2},
  stepTitleRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.sm},
  stepTitle: {...typography.labelMd, color: colors.primary},
  stepBody: {...typography.bodySm, color: colors.textMuted, lineHeight: 17},
  freeBadge: {
    backgroundColor: colors.primaryFixed,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  freeBadgeText: {...typography.labelSm, color: colors.primary},

  footer: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
    backgroundColor: colors.background,
    borderTopWidth: 1,
    borderTopColor: colors.borderSubtle,
    gap: spacing.sm,
  },
  footnote: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
  },
});

export default SavingsSummary;